/* ─────────────────────────────────────────────────────────────────────────────
   PGA Yorkshire — Global Parallax Engine
   ─────────────────────────────────────────────────────────────────────────────

   HOW TO USE
   ──────────
   Add data-parallax to any section wrapper that contains a full-bleed
   background image. The engine finds the img/video inside, scales it up
   slightly, and translates it on scroll — creating a floating depth effect.

   Basic:
     <div data-parallax> ... <img class="my-bg-image"> ... </div>

   Custom speed (0.1 = subtle, 0.3 = strong, default 0.2):
     <div data-parallax data-parallax-speed="0.15"> ... </div>

   Target a specific child image by class:
     <div data-parallax data-parallax-target=".my-specific-img"> ... </div>

   REQUIREMENTS ON THE CONTAINER
   ──────────────────────────────
   The container must have overflow: hidden (the engine sets this automatically).
   The target image must be position: absolute (the engine sets this too).

   The engine scales the image to 120% height and re-centres it so there is
   always visual buffer above and below — no white gaps on any screen size.

   REDUCED MOTION
   ──────────────
   Fully respects prefers-reduced-motion — parallax is disabled, image
   reverts to normal object-fit: cover fill.
──────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var items = [];     // { el, img, speed }
  var rafId = null;
  var ticking = false;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Set up one parallax item ── */
  function setup(container) {
    if (container._parallaxInit) return;
    container._parallaxInit = true;

    var targetSel = container.dataset.parallaxTarget;
    var img = targetSel
      ? container.querySelector(targetSel)
      : container.querySelector('img, video');

    if (!img) return;

    var speed = parseFloat(container.dataset.parallaxSpeed) || 0.2;

    if (REDUCED_MOTION) {
      /* No animation — ensure image still fills container */
      img.style.height = '100%';
      img.style.top    = '0';
      return;
    }

    /* Container needs overflow: hidden for clipping */
    container.style.overflow = 'hidden';

    /* Scale image so there's room to shift without gaps.
       extra = speed * 100 (e.g. 0.2 speed → 20% extra height, 10% top/bottom buffer) */
    var extra   = Math.round(speed * 100);
    var overage = Math.round(extra / 2);

    img.style.position      = 'absolute';
    img.style.top           = '-' + overage + '%';
    img.style.left          = '0';
    img.style.width         = '100%';
    img.style.height        = (100 + extra) + '%';
    img.style.objectFit     = 'cover';
    img.style.willChange    = 'transform';
    img.style.transform     = 'translateY(0px)';
    img.style.pointerEvents = 'none';

    items.push({ el: container, img: img, speed: speed, extra: extra });
  }

  /* ── Calculate and apply transforms ── */
  function updateAll() {
    var viewH = window.innerHeight;

    items.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();

      /* Skip if completely off-screen */
      if (rect.bottom < -100 || rect.top > viewH + 100) return;

      /* Progress: 0 when element bottom is at viewport bottom,
                   1 when element top is at viewport top */
      var totalTravel = viewH + rect.height;
      var scrolled    = viewH - rect.top;
      var progress    = Math.min(1, Math.max(0, scrolled / totalTravel));

      /* Offset: negative = image up (parallax effect as section enters from below),
                 positive = image down (section scrolling off top) */
      var maxShift = (item.extra / 100) * rect.height * 0.5;
      var offset   = (progress - 0.5) * maxShift * 2 * -1;

      item.img.style.transform = 'translateY(' + offset.toFixed(2) + 'px)';
    });

    ticking = false;
  }

  /* ── Scroll listener with RAF throttle ── */
  function onScroll() {
    if (ticking) return;
    ticking = true;
    rafId = requestAnimationFrame(updateAll);
  }

  /* ── Init: scan for [data-parallax] containers ── */
  function init(root) {
    root = root || document;
    root.querySelectorAll('[data-parallax]').forEach(function (el) {
      setup(el);
    });
    /* Also auto-detect image-heading sections (they have inline bg images) */
    root.querySelectorAll('.image-heading-section').forEach(function (el) {
      if (!el.hasAttribute('data-parallax')) {
        el.setAttribute('data-parallax', '');
        el.dataset.parallaxSpeed = '0.18';
        setup(el);
      }
    });

    if (items.length && !REDUCED_MOTION) {
      window.addEventListener('scroll', onScroll, { passive: true });
      updateAll(); /* Set initial position */
    }
  }

  /* ── Startup ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  window.ParallaxEngine = { refresh: init };
})();
